import React from 'react';
import { Dialog, DialogContent, IconButton, TextField, InputAdornment, FormLabel, CircularProgress, Checkbox, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { doc, getDoc, Timestamp } from 'firebase/firestore';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { RootState, useAppSelector } from '../../../state/state';
import { useAppDispatch } from '../../../state/state';
import { closeModal, toggleLoading } from '../../../state/ModalSlice/ModalSlice';
import { useBackend } from '../../../hooks/backend/useBackend';
import { db, SECRETS_ENV } from '../../../Config/firebase';
import { toast } from 'react-toastify';
import { SubscriptionType } from '../../../enums';
import { selectEffectiveUID } from '../../../state/UserStatesV2/userSliceV2';
import { logAction } from '../utilityFunctions';
import { ActionType } from '../../../enums';

interface Item {
    name: string;
    price: string;
    quantity: string;
}

// Professional currency formatting function
const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(amount).replace('₹', '₹ ');
};


const secretCloudTaskToken = import.meta.env.VITE_SECRET_CLOUD_TASK_TOKEN || process.env.VITE_SECRET_CLOUD_TASK_TOKEN



export default function MMEditOrderModal() {
    const Dispatch = useAppDispatch();
    const ServiceProviderID = useAppSelector(selectEffectiveUID)
    const { uid: spId, display_name: spName } = useAppSelector((state: RootState) => state.userSliceV2?.data) || {}
    const open = useAppSelector((state: RootState) => state.ModalSlice.MMEditOrderModalOpen);
    const OrderId = useAppSelector((state: RootState) => state.ArbitrarySlice.ForMMEditOrderModal.orderId);
    const subscriptionId = useAppSelector((state: RootState) => state.ArbitrarySlice.ForMMEditOrderModal.subscriptionId);
    const [cyclePriceArray, setCyclePriceArray] = React.useState<any>([]);
    const [selectedCyclePlan, setSelectedCyclePlan] = React.useState<any>(null);
    const [amountPaid, setAmountPaid] = React.useState<string>("");
    const [startDate, setStartDate] = React.useState<Date>(new Date());
    const [endDate, setEndDate] = React.useState<Date>(new Date());
    const [align2monthlyBoundary, setAlign2monthlyBoundary] = React.useState<boolean>(false);
    const [isPaid, setIsPaid] = React.useState(false);
    const [cancelOrder, setCancelOrder] = React.useState<boolean>(false);
    const [prevPrice, setPrevPrice] = React.useState<number | null>(null);
    const [modalError, setModalError] = React.useState({
        OrderAmount: false,
    });
    const [studentName, setStudentName] = React.useState<string>("");
    const [subscriptionName, setSubscriptionName] = React.useState<string>("");
    const isLoading = useAppSelector((state: RootState) => state.ModalSlice.isLoading);
    const [subscriptionType, setSubscriptionType] = React.useState<string>("");
    const [platformFees, setPlatformFees] = React.useState<number>(0);
    const [livesubscriptionId, setLivesubscriptionId] = React.useState<string>("");
    const [cycleName, setCycleName] = React.useState<string>("");
    const [invoiceLink, setInvoiceLink] = React.useState<string>("");
    const [receiptLink, setReceiptLink] = React.useState<string>("");

    // Items state
    const [items, setItems] = React.useState<Item[]>([]);
    const [itemErrors, setItemErrors] = React.useState<{ name: boolean, price: boolean, quantity: boolean }[]>([]);
    const [bQuickCollect, setBQuickCollect] = React.useState<boolean>(false);
    const [isOrderCancelled, setIsOrderCancelled] = React.useState<boolean>(false);
    const [orderNote, setOrderNote] = React.useState<string>("");

    const { update } = useBackend(`/ServiceProviders/${ServiceProviderID}/Orders`)

    function handleClose() {
        resetStates();
        Dispatch(closeModal("MMEditOrderModalOpen"));
    }

    function resetStates() {
        setAmountPaid("");
        setSelectedCyclePlan(null);
        setCyclePriceArray([]);
        setStartDate(new Date());
        setEndDate(new Date());
        setCancelOrder(false);
        setModalError({
            OrderAmount: false,
        });
        setItems([]);
        setItemErrors([]);
        setBQuickCollect(false);
        setIsOrderCancelled(false);
        setOrderNote("");
    }

    const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        if (!value || /^\d*\.?\d*$/.test(value)) {
            setAmountPaid(value);
        }
    };

    // Calculate total for an item
    const calculateItemTotal = (price: string, quantity: string) => {
        const priceNum = parseFloat(price) || 0;
        const quantityNum = parseFloat(quantity) || 0;
        return priceNum * quantityNum;
    };

    // Calculate grand total from items
    const calculateGrandTotal = () => {
        return items.reduce((total, item) => {
            return total + calculateItemTotal(item.price, item.quantity);
        }, 0);
    };

    // Calculate overall total for submission: amountPaid + (sum of item prices)
    const calculateOverallTotal = () => {
        const baseAmount = parseFloat(amountPaid) || 0;
        const itemsTotal = calculateGrandTotal();
        return baseAmount + itemsTotal;
    };

    // Check if current item is complete
    const isItemComplete = (item: Item) => {
        return item.name.trim() !== '' && item.price.trim() !== '' && item.quantity.trim() !== '';
    };

    // Check if can add new item
    const canAddItem = () => {
        if (items.length === 0) return true;
        const lastItem = items[items.length - 1];
        return isItemComplete(lastItem);
    };

    // Update item field
    const updateItem = (index: number, field: keyof Item, value: string) => {
        setItems(prev => prev.map((item, i) =>
            i === index ? { ...item, [field]: value } : item
        ));
    };

    // Add new item
    const addItem = () => {
        if (canAddItem()) {
            setItems(prev => [...prev, { name: '', price: '', quantity: '1' }]);
            setItemErrors(prev => [...prev, { name: false, price: false, quantity: false }]);
        }
    };

    // Remove item
    const removeItem = (index: number) => {
        if (items.length > 1) {
            setItems(prev => prev.filter((_, i) => i !== index));
            setItemErrors(prev => prev.filter((_, i) => i !== index));
        } else if (items.length === 1 && !bQuickCollect) {
            // Only allow removing the last item if it's not a Quick Collect order
            setItems([]);
            setItemErrors([]);
        }
        // For Quick Collect orders, we don't allow removing the last item
    };

    // Handle price input (only numbers and decimal)
    const handlePriceChange = (index: number, value: string) => {
        if (/^\d*\.?\d*$/.test(value)) {
            updateItem(index, 'price', value);
        }
    };

    // Handle quantity input (positive numbers including decimals, not zero)
    const handleQuantityChange = (index: number, value: string) => {
        if (/^\d*\.?\d*$/.test(value) && value !== '0' && value !== '0.') {
            updateItem(index, 'quantity', value);
        }
    };

    // Check errors for items
    const checkItemErrors = () => {
        let hasErrors = false;
        const newItemErrors: { name: boolean, price: boolean, quantity: boolean }[] = [];

        items.forEach((item, index) => {
            const errors = {
                name: item.name.trim() === '',
                price: parseFloat(item.price) < 6,
                quantity: parseFloat(item.quantity) < 1
            };

            if (errors.name || errors.price || errors.quantity) {
                hasErrors = true;
            }

            newItemErrors[index] = errors;
        });

        setItemErrors(newItemErrors);
        return hasErrors;
    };

    const getAlignedEndDate = (start: Date): Date => {
        if (subscriptionType === SubscriptionType.Count) {
            const validityDays = Number(selectedCyclePlan.validity) || 0;
            const newEndDate = new Date(start);
            newEndDate.setDate(start.getDate() + validityDays);
            return newEndDate;
        } else if (subscriptionType === SubscriptionType.MultiMonth) {
            const newEndDate = new Date(start);
            newEndDate.setDate(start.getDate() + (selectedCyclePlan?.validity || 0));
            return newEndDate;
        } else {
            return new Date();
        }
    };

    React.useEffect(() => {
        if (selectedCyclePlan && startDate) {
            const newEndDate = getAlignedEndDate(startDate);
            setEndDate(newEndDate);
        }
    }, [selectedCyclePlan, startDate]);

    const { axiosPostRequest } = useBackend("")

    async function handleAPICall() {
        Dispatch(toggleLoading(true))

        if (cancelOrder) {
            // Call the cancel order API
            const reqBody = {
                orderMonth: cycleName,
                serviceProviderId: ServiceProviderID,
                livesubscriptionId: livesubscriptionId,
                subscriptionId: subscriptionId,
                orderId: OrderId
            }

            try {

                const response = await axiosPostRequest(`${SECRETS_ENV.API_END_POINT}/api/writeOffExistingOrder`, reqBody)
                if (response.message !== "Request successful") {
                    Dispatch(toggleLoading(false))
                    return "error"
                }

                update(OrderId, {
                    orderNote: orderNote ? orderNote.trim() : ""
                })
                logAction({
                    action: ActionType.cancelOrder,
                    timestamp: Timestamp.now(),
                    platform: "web",
                    performedBy: {
                        spId: spId || "",
                        name: spName || ""
                    },
                    livesubscriptionId: livesubscriptionId,
                    context: {
                        orderId: OrderId,
                        prevPrice: prevPrice,
                        livesubscriptionId: livesubscriptionId,
                    },

                }, ServiceProviderID)


            } catch (error) {
                Dispatch(toggleLoading(false))
                return "error"
            } finally {
                Dispatch(toggleLoading(false))
            }
            return "success"
        } else {
            // Regular update order API
            if (!selectedCyclePlan || !amountPaid) return "error";

            const updateData = {
                providerId: ServiceProviderID,
                subscriptionId: subscriptionId,
                orderId: OrderId,
                billingCycle: subscriptionType === SubscriptionType.Count ? SubscriptionType.Count : selectedCyclePlan.billingCycle,
                billingStartTime: Timestamp.fromDate(startDate),
                price: Number(calculateOverallTotal()),
                priceWithCharges: 0,
                bRaiseInvoice: false,
                pauseDays: selectedCyclePlan.pauseDays || 0,
                secretCloudTaskToken: secretCloudTaskToken,
                count: selectedCyclePlan.count || 0,
                validity: selectedCyclePlan.validity || 0,
                items: items.map(item => ({
                    name: item.name,
                    price: Number(item.price),
                    quantity: Number(item.quantity)
                })),
            };

            try {
                const response = await axiosPostRequest(`${SECRETS_ENV.API_END_POINT}/api/editOrderMultiMonth`, updateData)
                if (response.message !== "Request successful") {
                    Dispatch(toggleLoading(false))
                    return "error"
                }

                logAction({
                    action: ActionType.editOrder,
                    timestamp: Timestamp.now(),
                    platform: "web",
                    performedBy: {
                        spId: spId || "",
                        name: spName || ""
                    },
                    livesubscriptionId: livesubscriptionId,
                    context: {
                        orderId: OrderId,
                        prevPrice: prevPrice,
                        livesubscriptionId: livesubscriptionId,
                        newPrice: Number(calculateOverallTotal()),
                        baseAmount: Number(amountPaid),
                        itemsTotal: calculateGrandTotal(),
                        bQuickCollect: bQuickCollect,
                    },
                }, ServiceProviderID)

            } catch (error) {
                Dispatch(toggleLoading(false))
                return "error"
            } finally {
                Dispatch(toggleLoading(false))
            }
            return "success"
        }
    }

    function CheckErrors(): boolean {
        let localerror: boolean = false
        if (cancelOrder) {
            return localerror
        }

        // For non-Quick Collect orders, check base amount
        if (!bQuickCollect) {
            if (!amountPaid || amountPaid.trim() === "" || Number(amountPaid) < 6) {
                localerror = true
                setModalError((prev) => {
                    return { ...prev, OrderAmount: true }
                })
            } else {
                setModalError((prev) => {
                    return { ...prev, OrderAmount: false }
                })
            }
        }

        // Check if overall total is valid
        if (calculateOverallTotal() <= 0) {
            localerror = true
        }

        // Check item errors
        const itemsHaveErrors = checkItemErrors();
        if (itemsHaveErrors) {
            localerror = true;
        }

        // For Quick Collect orders, ensure at least one item exists
        if (bQuickCollect && items.length === 0) {
            localerror = true;
        }

        return localerror
    }

    async function onSubmit(e: React.FormEvent) {
        e.preventDefault();
        const error = CheckErrors();
        if (error) {
            return;
        }
        const response = await handleAPICall();
        if (response === "success") {
            toast.success("Order updated successfully")
            handleClose();
        } else {
            toast.error("Error updating Order details")
            handleClose();
        }
    }

    React.useEffect(() => {
        const fetchData = async () => {
            if (OrderId) {
                try {

                    const orderDocRef = doc(db, `ServiceProviders/${ServiceProviderID}/Orders/${OrderId}`);
                    const orderDoc = await getDoc(orderDocRef);

                    if (orderDoc.exists()) {
                        const orderData = orderDoc.data();
                        const isQuickCollect = Boolean(orderData.bQuickCollect);

                        setStudentName(orderData.studentName);
                        setSubscriptionName(orderData.subscriptionName);
                        setSubscriptionType(orderData.subscriptionType);
                        setLivesubscriptionId(orderData.livesubscriptionId || "");
                        setCycleName(orderData.cycleName || "");
                        setIsPaid(orderData.payment_status === "Paid");
                        setCancelOrder(false);
                        setCyclePriceArray(orderData.assignedPlans);
                        const matchingPlan = orderData.assignedPlans?.[orderData.assignedPlanIndex || 0]
                        setSelectedCyclePlan(matchingPlan);
                        setBQuickCollect(isQuickCollect);
                        setIsOrderCancelled(orderData.orderType === "writeoff");

                        // Handle items first to calculate their total
                        let itemsArray: Item[] = [];
                        if (orderData.items && orderData.items.length > 0) {
                            itemsArray = orderData.items.map((item: any) => ({
                                name: item.name ?? '',
                                price: item.price !== undefined && item.price !== null ? String(item.price) : '',
                                quantity: item.quantity !== undefined && item.quantity !== null ? String(item.quantity) : '1',
                            }));
                            setItems(itemsArray);
                            setItemErrors(itemsArray.map(() => ({ name: false, price: false, quantity: false })));
                        } else if (isQuickCollect) {
                            // Quick Collect orders must have at least one item
                            itemsArray = [{ name: '', price: '', quantity: '1' }];
                            setItems(itemsArray);
                            setItemErrors([{ name: false, price: false, quantity: false }]);
                        } else {
                            setItems([]);
                            setItemErrors([]);
                        }

                        // Calculate items total from current items
                        const itemsTotal = itemsArray.reduce((total, item) => {
                            const priceNum = parseFloat(item.price) || 0;
                            const quantityNum = parseFloat(item.quantity) || 0;
                            return total + (priceNum * quantityNum);
                        }, 0);

                        // amountPaid = chargeable - (sum of item prices)
                        const baseOrderAmount = orderData.chargeable - itemsTotal;

                        setAmountPaid(baseOrderAmount.toString());
                        setPrevPrice(orderData.chargeable);
                        if (orderData.BillingStartTime) {
                            setStartDate(orderData.BillingStartTime.toDate());
                        }
                        if (orderData.BillingEndTime) {
                            setEndDate(orderData.BillingEndTime.toDate());
                        }
                        if (orderData.platformFees) {
                            setPlatformFees(orderData.platformFees);
                        }
                        setInvoiceLink(orderData.pdf_link || "");
                        setReceiptLink(orderData.receipt_link || "");
                        setOrderNote(orderData.orderNote || "");
                    }

                } catch (error) {
                    console.error("Error fetching data:", error);
                }
            }
        };

        fetchData();
    }, [subscriptionId, OrderId, ServiceProviderID, open]);

    return (
        <Dialog
            open={open}
            maxWidth="sm"
            PaperProps={{
                sx: { borderRadius: '10px' }
            }}
            fullWidth
        >
            <DialogContent sx={{ paddingX: "60px", paddingY: "40px" }} className="bg-white">
                <div className="header pb-[10px] ">
                    <p className="text-cf_heading font-[600]">{isPaid ? 'Order Summary' : 'Edit Order Details'}</p>
                    <div className="pt-0">
                        <p>Name : {studentName}</p>
                        <p>Subscription : {subscriptionName}</p>
                        <p>Cycle : {cycleName}</p>

                    </div>
                    {isPaid && (
                        <>
                            <p className={`text font-semibold mt-2 ${isOrderCancelled ? "text-red-500" : "text-cc_success"}`}>
                                {isOrderCancelled ? "This order has been cancelled." : "This order has already been paid."}
                            </p>
                            <p className="text-sm mt-1">
                                Platform Fee: ₹{platformFees}
                            </p>
                        </>
                    )}
                </div>

                <div className="absolute right-4 top-4">
                    <IconButton onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </div>

                {/* Only show order details if not cancelled */}
                {!isOrderCancelled && (
                    <div className="space-y-4">
                        {!bQuickCollect && <>
                            {subscriptionType === SubscriptionType.MultiMonth && (
                                <div>
                                    <FormLabel component="legend" sx={{ color: 'black', mb: 1 }}>Selected Plan</FormLabel>

                                    <div className="grid grid-cols-4 gap-4 mb-2 pl-10 pr-4">
                                        <div className="text-gray-500 text-sm">Plan</div>
                                        <div className="text-gray-500 text-sm">Price</div>
                                        <div className="text-gray-500 text-sm">Duration (Days)</div>
                                    </div>

                                    <div className="grid grid-cols-4 gap-4 w-full bg-white rounded-lg shadow-sm pl-10 mb-4">
                                        <div>{selectedCyclePlan?.billingCycle}</div>
                                        <div>₹{selectedCyclePlan?.price}</div>
                                        <div>{selectedCyclePlan?.validity} days</div>
                                    </div>

                                </div>
                            )}

                            {subscriptionType === SubscriptionType.Count && (
                                <div>
                                    <FormLabel component="legend" className="text-gray-800 font-medium mb-2">
                                        Selected Plan
                                    </FormLabel>

                                    <div className="grid grid-cols-4 gap-4 mb-2 pl-10 pr-4">
                                        <div className="text-gray-500 text-sm">Classes</div>
                                        <div className="text-gray-500 text-sm">Price</div>
                                        <div className="text-gray-500 text-sm">Validity (Days)</div>
                                        <div></div>
                                    </div>

                                    <div className="grid grid-cols-4 gap-4 w-full bg-white rounded-lg shadow-sm p-2 pl-10">
                                        <div>{selectedCyclePlan?.count} classes</div>
                                        <div>₹{selectedCyclePlan?.price}</div>
                                        <div>{selectedCyclePlan?.validity} days</div>
                                    </div>

                                </div>
                            )}

                            {
                                isPaid ? (
                                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                                        <p className="text-sm text-gray-600 mb-1">Base Order Amount</p>
                                        <p className="text-lg font-semibold">Rs.{parseFloat(amountPaid || "0").toFixed(2)}</p>
                                    </div>
                                ) : (
                                    <TextField
                                        value={amountPaid}
                                        onChange={handleAmountChange}
                                        disabled={isPaid}
                                        size="small"
                                        label="Amount"
                                        variant="outlined"
                                        error={modalError.OrderAmount}
                                        helperText={modalError.OrderAmount ? "Order amount must be minimum ₹6" : ""}
                                        fullWidth
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': { borderColor: 'grey', borderRadius: '10px' },
                                                '& .MuiInputBase-input': { fontSize: '14px' },
                                            }
                                        }}
                                    />
                                )
                            }


                            {/* {!isPaid && (
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <p className="text-xs font-medium mb-1 text-gray-600">Billing Start Date</p>
                                            <DatePicker
                                                value={dayjs(startDate)}
                                                onChange={(newValue) => !isPaid && setStartDate(newValue ? newValue.toDate() : new Date())}
                                                disabled={isPaid}
                                                format="DD/MM/YYYY"
                                                slotProps={{
                                                    textField: {
                                                        size: "small",
                                                        sx: {
                                                            '& .MuiOutlinedInput-root': {
                                                                '& fieldset': {
                                                                    borderColor: 'grey',
                                                                    borderRadius: '10px',
                                                                },
                                                                '& .MuiInputBase-input': {
                                                                    fontSize: '14px',
                                                                },
                                                            },
                                                        }
                                                    }
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <p className="text-xs font-medium mb-1 text-gray-600">
                                                Billing End Date {align2monthlyBoundary && selectedCyclePlan?.billingCycle !== "FlexibleMonthly" && subscriptionType !== "Count" ? "(Aligned to month boundary)" : ""}
                                            </p>
                                            <DatePicker
                                                value={dayjs(endDate)}
                                                onChange={(newValue) => setEndDate(newValue ? newValue.toDate() : new Date())}
                                                format="DD/MM/YYYY"
                                                disabled
                                                slotProps={{
                                                    textField: {
                                                        size: "small",
                                                        sx: {
                                                            '& .MuiOutlinedInput-root': {
                                                                '& fieldset': {
                                                                    borderColor: 'grey',
                                                                    borderRadius: '10px',
                                                                },
                                                                '& .MuiInputBase-input': {
                                                                    fontSize: '14px',
                                                                },
                                                            },
                                                        }
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>
                                </LocalizationProvider>
                            )} */}
                        </>}
                        {/* Items Section */}
                        <div className="space-y-4 mt-6">
                            <div className="flex justify-between items-center">
                                {(!isPaid || items.length > 0) && <div className="flex items-center gap-3">
                                    <h3 className="text-lg font-semibold text-gray-900">
                                        {bQuickCollect ? "Items" : "Additional Items"}
                                    </h3>
                                    {bQuickCollect && (
                                        <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-md font-medium">
                                            Quick Collect
                                        </span>
                                    )}
                                    {items.length === 0 && !isPaid && (
                                        <button
                                            onClick={addItem}
                                            className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                                        >
                                            + Add Items
                                        </button>
                                    )}
                                </div>}
                                {items.length > 0 && (
                                    <div className="text-right">
                                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Items Total</p>
                                        <p className="text-lg font-semibold text-gray-900 tabular-nums">{formatCurrency(calculateGrandTotal())}</p>
                                    </div>
                                )}
                            </div>

                            {items.length > 0 && (
                                <>
                                    {items.map((item, index) => (
                                        <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
                                            <div className="flex justify-between items-center mb-3">
                                                <h4 className="font-medium text-gray-700 text-sm">Item {index + 1}</h4>
                                                <div className="flex items-center gap-4">
                                                    {calculateItemTotal(item.price, item.quantity) > 0 && (
                                                        <div className="text-right">
                                                            <div className="bg-blue-100 text-blue-800 px-3 py-1.5 rounded-lg border border-blue-200">
                                                                <span className="text-sm font-semibold tabular-nums">
                                                                    {formatCurrency(calculateItemTotal(item.price, item.quantity))}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {!isPaid && !(bQuickCollect && items.length === 1) && (
                                                        <button
                                                            onClick={() => removeItem(index)}
                                                            className="text-red-500 hover:text-red-700 text-xs font-medium px-2 py-1 rounded hover:bg-red-50 transition-colors"
                                                        >
                                                            Remove
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                                <TextField
                                                    value={item.name}
                                                    size='small'
                                                    onChange={(e) => updateItem(index, 'name', e.target.value)}
                                                    label="Item Name"
                                                    variant="outlined"
                                                    fullWidth
                                                    placeholder="Enter item name"
                                                    disabled={isPaid}
                                                    error={itemErrors[index]?.name || false}
                                                    helperText={itemErrors[index]?.name ? "Item name is required" : ""}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            '& fieldset': {
                                                                borderColor: '#e5e7eb',
                                                                borderRadius: '8px'
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: '#d1d5db',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#3b82f6',
                                                            },
                                                            '& .MuiInputBase-input': {
                                                                fontSize: '14px',
                                                            },
                                                        },
                                                        fontWeight: 400,
                                                    }}
                                                />

                                                <TextField
                                                    value={item.price}
                                                    size='small'
                                                    onChange={(e) => handlePriceChange(index, e.target.value)}
                                                    label="Price"
                                                    variant="outlined"
                                                    fullWidth
                                                    placeholder="0.00"
                                                    disabled={isPaid}
                                                    error={itemErrors[index]?.price || false}
                                                    helperText={itemErrors[index]?.price ? "Minimum ₹6 required" : ""}
                                                    InputProps={{
                                                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                                    }}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            '& fieldset': {
                                                                borderColor: '#e5e7eb',
                                                                borderRadius: '8px'
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: '#d1d5db',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#3b82f6',
                                                            },
                                                            '& .MuiInputBase-input': {
                                                                fontSize: '14px',
                                                                fontVariantNumeric: 'tabular-nums',
                                                            },
                                                        },
                                                        fontWeight: 400,
                                                    }}
                                                />

                                                <TextField
                                                    value={item.quantity}
                                                    size='small'
                                                    onChange={(e) => handleQuantityChange(index, e.target.value)}
                                                    label="Quantity"
                                                    variant="outlined"
                                                    fullWidth
                                                    placeholder="1.0"
                                                    disabled={isPaid}
                                                    error={itemErrors[index]?.quantity || false}
                                                    helperText={itemErrors[index]?.quantity ? "Minimum 1 required" : ""}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            '& fieldset': {
                                                                borderColor: '#e5e7eb',
                                                                borderRadius: '8px'
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: '#d1d5db',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#3b82f6',
                                                            },
                                                            '& .MuiInputBase-input': {
                                                                fontSize: '14px',
                                                                fontVariantNumeric: 'tabular-nums',
                                                            },
                                                        },
                                                        fontWeight: 400,
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    ))}

                                    {!isPaid && (
                                        <button
                                            onClick={addItem}
                                            disabled={!canAddItem()}
                                            className={`w-full py-3 border-2 border-dashed rounded-lg text-sm font-medium transition-all ${canAddItem()
                                                ? 'border-blue-200 text-blue-600 hover:border-blue-300 hover:bg-blue-50 cursor-pointer'
                                                : 'border-gray-200 text-gray-400 cursor-not-allowed'
                                                }`}
                                        >
                                            + Add Item
                                        </button>
                                    )}
                                </>
                            )}

                            {/* Order Note Section - Show for paid orders */}
                            {isPaid && !isOrderCancelled && (
                                <div className="mt-4">
                                    <TextField
                                        value={orderNote}
                                        onChange={(e) => setOrderNote(e.target.value)}
                                        size="small"
                                        label="Order Notes"
                                        variant="outlined"
                                        fullWidth
                                        multiline
                                        rows={3}
                                        placeholder="Add notes about this order..."
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: 'grey',
                                                    borderRadius: '10px'
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'grey',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: 'skyblue',
                                                },
                                                '& .MuiInputBase-input': {
                                                    fontSize: '14px',
                                                },
                                            },
                                            fontWeight: 400,
                                        }}
                                    />
                                </div>
                            )}

                            {/* Overall Total Section - Show for all orders */}
                            {calculateOverallTotal() > 0 && (
                                <div className={`bg-gradient-to-r ${isPaid ? 'from-green-50 to-emerald-50 border-green-100' : 'from-blue-50 to-indigo-50 border-blue-100'} border rounded-lg p-4 mt-4`}>
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <p className="text-sm text-gray-800 font-semibold tracking-wide">
                                                {isPaid ? "Total Paid Amount" : "Total Chargeable Amount"}
                                            </p>
                                            <div className="text-xs text-gray-800 mt-1">
                                                {!bQuickCollect && parseFloat(amountPaid) > 0 && (
                                                    <span>Base: {formatCurrency(parseFloat(amountPaid))}</span>
                                                )}
                                                {items.length > 0 && calculateGrandTotal() > 0 && (
                                                    <span>{(!bQuickCollect && parseFloat(amountPaid) > 0) ? ' + ' : ''}Items: {formatCurrency(calculateGrandTotal())}</span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-2xl font-bold text-gray-900 tabular-nums">
                                                {formatCurrency(calculateOverallTotal())}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Cancel Order Checkbox - Only shown for pending orders */}
                        {!isPaid && (
                            <div className="flex items-center mt-4">
                                <Checkbox
                                    checked={cancelOrder}
                                    onChange={(e) => setCancelOrder(e.target.checked)}
                                    sx={{ color: 'red', '&.Mui-checked': { color: 'red' } }}
                                />
                                <span className="text-red-600 font-medium">Cancel Order?</span>
                                <Tooltip
                                    title="The order will be cancelled and all the above selections will become invalid"
                                    placement="top"
                                    arrow
                                >
                                    <IconButton size="small">
                                        <HelpOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        )}

                        {/* Notes Section - Only shown when cancel order is checked */}
                        {cancelOrder && (
                            <div className="mt-4">
                                <TextField
                                    value={orderNote}
                                    onChange={(e) => setOrderNote(e.target.value)}
                                    size="small"
                                    label="Cancellation Notes (Optional)"
                                    variant="outlined"
                                    fullWidth
                                    multiline
                                    rows={3}
                                    placeholder="Add notes about why this order is being cancelled..."
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: 'grey',
                                                borderRadius: '10px'
                                            },
                                            '&:hover fieldset': {
                                                borderColor: 'grey',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: 'skyblue',
                                            },
                                            '& .MuiInputBase-input': {
                                                fontSize: '14px',
                                            },
                                        },
                                        fontWeight: 400,
                                    }}
                                />
                            </div>
                        )}
                    </div>
                )}

                <div className="links flex flex-col mt-4 gap-4">
                    {invoiceLink && (
                        <div
                            className="ml-2 flex gap-2 items-center cursor-pointer"
                            onClick={() => window.open(invoiceLink, '_blank')}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                                stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                className="feather feather-link-2">
                                <path d="M15 7h3a5 5 0 0 1 5 5 5 5 0 0 1-5 5h-3m-6 0H6a5 5 0 0 1-5-5 5 5 0 0 1 5-5h3"></path>
                                <line x1="8" y1="12" x2="16" y2="12"></line>
                            </svg>
                            <p className="text-cc_link underline underline-offset-2">Invoice Link</p>
                        </div>
                    )}

                    {receiptLink && (
                        <div
                            className="ml-2 flex gap-2 items-center cursor-pointer"
                            onClick={() => window.open(receiptLink, '_blank')}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                                stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                className="feather feather-link-2">
                                <path d="M15 7h3a5 5 0 0 1 5 5 5 5 0 0 1-5 5h-3m-6 0H6a5 5 0 0 1-5-5 5 5 0 0 1 5-5h3"></path>
                                <line x1="8" y1="12" x2="16" y2="12"></line>
                            </svg>
                            <p className="text-cc_link underline underline-offset-2">Receipt Link</p>
                        </div>
                    )}
                </div>

                {/* Show different buttons based on order status */}
                {isOrderCancelled ? (
                    <div className="flex justify-center">
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                handleClose()
                            }}
                            className="w-full bg-cc_brand mt-8 max-w-[200px] text-white p-1.5 rounded-lg border-gray-500 flex justify-center items-center">
                            Close
                        </button>
                    </div>
                ) : (
                    <div className="flex gap-2 pt-4">
                        <button
                            onClick={async (e) => {
                                e.preventDefault();
                                if (isPaid && orderNote.trim()) {
                                    Dispatch(toggleLoading(true));
                                    try {
                                        await update(OrderId, {
                                            orderNote: orderNote.trim()
                                        });
                                        toast.success("Order note updated successfully");
                                    } catch (error) {
                                        toast.error("Failed to update order note");
                                    } finally {
                                        Dispatch(toggleLoading(false));
                                    }
                                }
                                handleClose();
                            }}
                            disabled={isLoading}
                            className={`w-full bg-cc_bg-tertiary border border-solid border-gray-500 p-1.5 rounded-lg ${isLoading ? "cursor-not-allowed" : ""} `}>
                            {isLoading && isPaid ? (
                                <>
                                    <CircularProgress sx={{ color: "black" }} size={20} />
                                    <span className="ml-2">Saving</span>
                                </>
                            ) : (isPaid ? 'Okay' : 'Cancel')}
                        </button>
                        {!isPaid && (
                            <button disabled={isLoading}
                                onClick={onSubmit}
                                className={`w-full bg-cc_brand text-white p-1.5 rounded-lg border-gray-500 flex justify-center items-center ${isLoading ? "cursor-not-allowed" : ""} `}>{isLoading &&
                                    <CircularProgress sx={{ color: "white" }} size={20} />}
                                <p className={"ml-2"}>{isLoading ? "Loading" : "Update Order"}</p>
                            </button>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
